import { Crown } from 'lucide-react'
import {
  shouldShowBankerNudge,
  shouldShowMissedBankerRow,
} from '../../predictionCycle'
import type { Game, Prediction } from '../../types'

interface BankerPromptsProps {
  gameweek: number
  games: Game[]
  predictions: Record<string, Prediction>
  bankerGameId: string | null
  deadlinePassed: boolean
}

/** The two mutually-exclusive Banker rows above the fixture grid — the pre-deadline
 *  "pick your Banker" slot and the post-deadline "was not selected" row.
 * Extracted from GameweekGrid.tsx. */
function BankerPrompts({
  gameweek,
  games,
  predictions,
  bankerGameId,
  deadlinePassed,
}: BankerPromptsProps) {
  return (
    <>
      {/* An empty slot awaiting input, not a filled card: the dashes are
          an inset SVG rect rather than a CSS `border: 1px dashed` so the edge can be
          animated on its own — the grid breathes its opacity, which on the element
          itself would fade the title and hint too. pathLength normalises the perimeter
          to 100 so the dash rhythm holds at any bar width. */}
      {shouldShowBankerNudge(
        games,
        predictions,
        bankerGameId,
        deadlinePassed
      ) && (
        <div className="gameweek-grid__banker-slot">
          <svg
            className="gameweek-grid__banker-slot-dashes"
            aria-hidden="true"
            focusable="false"
          >
            <rect pathLength={100} />
          </svg>
          <p className="gameweek-grid__banker-slot-title">
            <Crown aria-hidden="true" />
            Pick your Banker for Gameweek {gameweek}
          </p>
          {/* Two facts, in the order they matter. Nothing else before the pick
              states the reward — the edit-screen button only says "Double pts" once you've
              already picked — so it leads. "No auto-pick" is the load-bearing fact: nothing
              assigns one at the deadline, and skipping now
              means playing the gameweek without a Banker at all. */}
          <p className="gameweek-grid__banker-slot-hint">
            Double points. No auto-pick.
          </p>
        </div>
      )}

      {/* The same flat box as the slot above, in the dimmed register:
          plain CSS dashes, no entrance animation, nothing marching — the deadline is
          gone, so there is no interaction left to advertise. */}
      {shouldShowMissedBankerRow(
        games,
        predictions,
        bankerGameId,
        deadlinePassed
      ) && (
        <div className="gameweek-grid__banker-missed">
          <Crown aria-hidden="true" />
          <p className="gameweek-grid__banker-missed-message">
            Banker was not selected
          </p>
        </div>
      )}
    </>
  )
}

export default BankerPrompts
